import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../common/api';

interface Event {
  id: string;
  patientId: string | null;
  patientExtId: string | null;
  s3Path: string | null;
  comment: string | null;
  success: boolean;
  noData: boolean;
  createdAt: string | null;
  fetchDate: string | null;
}

interface PaginatedEventSummary {
  events: Event[];
  page: number;
  pageSize: number;
  total: number;
}

interface PaginatedAuditLogSummary {
  events: Event[];
  page: number;
  pageSize: number;
  total: number;
}

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
}

interface EventQueryParams {
  sortKey?: string;
  sortOrder?: SortOrder;
  page?: number;
  pageSize?: number;
  patientId?: string;
  subCategory?: string;
}

interface AuditLogQueryParams {
  sortKey?: string;
  sortOrder?: SortOrder;
  page?: number;
  pageSize?: number;
  patientId?: string;
}

export interface EventsState {
  events: PaginatedEventSummary | null;
  auditlogs: PaginatedAuditLogSummary | null;
  loading: boolean;
  error: string | null;
}

const initialState: EventsState = {
  events: null,
  auditlogs: null,
  loading: false,
  error: null,
};

/**
 * Fetch the audit logs from the backend
 */
export const fetchAuditLogs = createAsyncThunk<PaginatedEventSummary, AuditLogQueryParams, { rejectValue: string }>(
  'events/fetchAuditLog',
  async (params: EventQueryParams = {}, thunkAPI) => {
    const { sortKey = 'id', sortOrder = SortOrder.Asc, page = 0, pageSize = 10 } = params;
    const extra_params: AuditLogQueryParams = {};
    if (params.patientId !== null) {
      extra_params.patientId = params.patientId;
    }
    try {
      const response = await api.get('/api/v1/events/auditlogs', {
        params: {
          sort: sortKey,
          order: sortOrder,
          page: Math.max(page, 0),
          pageSize: pageSize,
          ...extra_params,
        },
      });
      return response.data as PaginatedEventSummary;
    } catch (error) {
      return thunkAPI.rejectWithValue('Failed to load auditlogs');
    }
  },
);
const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action: PayloadAction<PaginatedEventSummary>) => {
        state.loading = false;
        state.auditlogs = action.payload;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load audtilogs';
      });
  },
});

export default eventsSlice.reducer;
